import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Zap, Clock, Flame, TrendingUp, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';

interface FlashSale {
  id: string;
  title: string;
  description: string;
  bundle_id: string;
  original_price: number;
  sale_price: number;
  discount_percentage: number;
  starts_at: string;
  ends_at: string;
  max_purchases: number;
  current_purchases: number;
  is_active: boolean;
}

interface CountdownTime {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  expired: boolean;
}

export function FlashSales() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [sales, setSales] = useState<FlashSale[]>([]);
  const [loading, setLoading] = useState(true);
  const [countdowns, setCountdowns] = useState<Record<string, CountdownTime>>({});

  useEffect(() => {
    fetchFlashSales();
  }, []);

  useEffect(() => {
    if (sales.length === 0) return;

    const interval = setInterval(() => {
      const newCountdowns: Record<string, CountdownTime> = {};
      
      sales.forEach(sale => {
        newCountdowns[sale.id] = calculateTimeLeft(sale.ends_at);
      });

      setCountdowns(newCountdowns);
    }, 1000);

    return () => clearInterval(interval);
  }, [sales]);

  const fetchFlashSales = async () => {
    try {
      setLoading(true);
      const now = new Date().toISOString();
      
      const { data, error } = await supabase
        .from('flash_sales')
        .select('*')
        .eq('is_active', true)
        .lte('starts_at', now)
        .gte('ends_at', now)
        .order('ends_at', { ascending: true });

      if (error) throw error;
      setSales(data || []);
    } catch (error) {
      console.error('Error fetching flash sales:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTimeLeft = (endsAt: string): CountdownTime => {
    const now = new Date().getTime();
    const end = new Date(endsAt).getTime();
    const difference = end - now;

    if (difference <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
    }

    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((difference % (1000 * 60)) / 1000),
      expired: false
    };
  };

  const handlePurchase = async (saleId: string, bundleId: string) => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to purchase flash sale bundles",
        variant: "destructive"
      });
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { bundle_id: bundleId }
      });

      if (error) throw error;
      
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Purchase error:', error);
      toast({
        title: "Error",
        description: "Failed to initiate checkout",
        variant: "destructive",
      });
    }
  };

  const formatCountdown = (time: CountdownTime) => {
    if (time.expired) return "EXPIRED";
    
    const parts = [];
    if (time.days > 0) parts.push(`${time.days}d`);
    if (time.hours > 0 || time.days > 0) parts.push(`${time.hours}h`);
    if (time.minutes > 0 || time.hours > 0 || time.days > 0) parts.push(`${time.minutes}m`);
    parts.push(`${time.seconds}s`);
    
    return parts.join(' ');
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2].map(i => (
          <Card key={i} className="animate-pulse">
            <CardHeader>
              <div className="h-6 bg-muted rounded w-3/4" />
            </CardHeader>
            <CardContent>
              <div className="h-32 bg-muted rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (sales.length === 0) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Flame className="h-6 w-6 text-orange-500 animate-pulse" />
        <h2 className="text-3xl font-bold bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
          ⚡ Flash Sales
        </h2>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {sales.map((sale) => {
          const countdown = countdowns[sale.id];
          const purchaseProgress = (sale.current_purchases / sale.max_purchases) * 100;
          const isLowStock = (sale.max_purchases - sale.current_purchases) <= 5;
          const isSoldOut = sale.current_purchases >= sale.max_purchases;

          return (
            <Card
              key={sale.id}
              className="relative overflow-hidden bg-gradient-to-br from-orange-500/5 to-red-500/5 border-orange-500/20 hover:border-orange-500/40 transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl"
            >
              {/* Animated Background Effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 to-red-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              {/* Discount Badge */}
              <div className="absolute top-4 right-4 z-10">
                <Badge className="bg-gradient-to-r from-orange-500 to-red-500 text-white text-lg font-bold px-3 py-1 animate-pulse">
                  -{sale.discount_percentage}%
                </Badge>
              </div>

              <CardHeader>
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-orange-500/10 rounded-lg">
                    <Zap className="h-6 w-6 text-orange-500" />
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-xl mb-1">{sale.title}</CardTitle>
                    <p className="text-sm text-muted-foreground">{sale.description}</p>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                {/* Countdown Timer */}
                <div className="bg-card/80 backdrop-blur-sm rounded-lg p-4 border border-orange-500/20">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="h-4 w-4" />
                      Time Remaining
                    </div>
                    {isLowStock && !isSoldOut && (
                      <Badge variant="destructive" className="text-xs animate-pulse">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        Low Stock
                      </Badge>
                    )}
                  </div>
                  <div className="text-3xl font-bold text-center">
                    {countdown ? (
                      <span className={countdown.expired ? "text-destructive" : "text-orange-500"}>
                        {formatCountdown(countdown)}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">Loading...</span>
                    )}
                  </div>
                </div>

                {/* Availability Progress */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Claimed</span>
                    <span className="font-semibold">
                      {sale.current_purchases} / {sale.max_purchases}
                    </span>
                  </div>
                  <Progress 
                    value={purchaseProgress} 
                    className="h-2"
                  />
                  <p className="text-xs text-muted-foreground text-center">
                    {sale.max_purchases - sale.current_purchases} left
                  </p>
                </div>

                {/* Price Section */}
                <div className="bg-card/80 backdrop-blur-sm rounded-lg p-4 border border-border/50">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm text-muted-foreground line-through">
                          ${sale.original_price.toFixed(2)}
                        </span>
                        <Badge variant="outline" className="text-xs">
                          Save ${(sale.original_price - sale.sale_price).toFixed(2)}
                        </Badge>
                      </div>
                      <div className="text-3xl font-bold text-orange-500">
                        ${sale.sale_price.toFixed(2)}
                      </div>
                    </div>
                    <TrendingUp className="h-8 w-8 text-green-500" />
                  </div>

                  <Button
                    className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white"
                    size="lg"
                    onClick={() => handlePurchase(sale.id, sale.bundle_id)}
                    disabled={isSoldOut || (countdown && countdown.expired)}
                  >
                    {isSoldOut ? (
                      "SOLD OUT"
                    ) : countdown?.expired ? (
                      "EXPIRED"
                    ) : (
                      <>
                        <Flame className="h-4 w-4 mr-2" />
                        Grab Deal Now
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
